import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { POLL_VOTE_POW } from '@/lib/stashstr';
import { verifyPow } from '@/lib/pow';

export interface PollOption {
  id: string;
  label: string;
}

export interface PollData {
  question: string;
  options: PollOption[];
  tally: Map<string, number>; // option id -> vote count
  totalVotes: number;
  hiddenVotes: number; // unproven votes
  hasVoted: boolean;
  myVote: string | undefined;
}

function optionIdFromTagValue(value: string | undefined): string {
  return value ?? '';
}

export function usePoll(pollId: string | undefined, myPubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['stashstr', 'poll', pollId ?? '', myPubkey ?? ''],
    queryFn: async (c) => {
      if (!pollId) throw new Error('No poll id');

      // NOTE: relays index lowercase tags only; uppercase filters (#E) are
      // best-effort for NIP-88 compat. The lowercase #e filter carries the load.
      const results = await nostr.query(
        [{ kinds: [1068], ids: [pollId], limit: 1 }, { kinds: [1018], '#E': [pollId], limit: 500 }, { kinds: [1018], '#e': [pollId], limit: 500 }],
        { signal: c.signal },
      );

      const pollEvent = results.find((e) => e.kind === 1068);
      if (!pollEvent) throw new Error('Poll not found');

      const question = pollEvent.content.trim();

      const options: PollOption[] = pollEvent.tags
        .filter(([t]) => t === 'option')
        .map(([, id, label]) => ({ id: optionIdFromTagValue(id), label: label ?? id ?? '' }))
        .filter((o) => o.label.length > 0);

      const votes = results.filter((e) => e.kind === 1018);

      // Deduplicate: latest vote per pubkey wins
      const latestByPubkey = new Map<string, NostrEvent>();
      for (const vote of votes) {
        const existing = latestByPubkey.get(vote.pubkey);
        if (!existing || vote.created_at > existing.created_at) {
          latestByPubkey.set(vote.pubkey, vote);
        }
      }

      const tally = new Map<string, number>();
      let hiddenVotes = 0;
      for (const vote of latestByPubkey.values()) {
        const optionTag = vote.tags.find(([t]) => t === 'option')?.[1];
        if (!optionTag) continue;
        if (!verifyPow(vote, POLL_VOTE_POW)) {
          hiddenVotes++;
          continue;
        }
        tally.set(optionTag, (tally.get(optionTag) ?? 0) + 1);
      }

      const myVoteEvent = myPubkey ? latestByPubkey.get(myPubkey) : undefined;
      const myVote = myVoteEvent?.tags.find(([t]) => t === 'option')?.[1];

      const totalVotes = [...tally.values()].reduce((a, b) => a + b, 0);

      return {
        question,
        options,
        tally,
        totalVotes,
        hiddenVotes,
        hasVoted: Boolean(myVote),
        myVote,
      } satisfies PollData;
    },
    staleTime: 15_000,
    enabled: Boolean(pollId),
  });
}
